/**
 * OverviewSection - Reusable wrapper for overview sections with cards
 *
 * Provides consistent layout for overview sections with heading and grid of info cards
 *
 * @example
 * ```tsx
 * <OverviewSection title="Overview">
 *   <InfoCard
 *     icon={<HiOutlineEnvelope size={18} />}
 *     title="Contact"
 *     items={[
 *       { label: "Email", value: user.email },
 *       { label: "Phone", value: user.phone },
 *     ]}
 *   />
 *   <InfoCard
 *     icon={<HiOutlineCalendar size={18} />}
 *     title="Dates"
 *     items={[
 *       { label: "Start Date", value: formatDate(startDate) },
 *       { label: "End Date", value: formatDate(endDate) },
 *     ]}
 *   />
 * </OverviewSection>
 * ```
 */

"use client";

import { Box, SimpleGrid } from "@chakra-ui/react";
import type { ReactNode } from "react";
import { Heading } from "../../ui/Heading";
import { useThemeTokens } from "@/lib/theme/tokens";

export interface OverviewSectionProps {
  /**
   * Section title (default: "Overview")
   */
  title?: string;
  /**
   * Grid columns configuration (default: { base: 1, md: 2, lg: 3 })
   */
  columns?: {
    base?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };
  /**
   * Grid spacing (default: 6)
   */
  spacing?: number;
  showTitle?: boolean;
  /**
   * Children (typically InfoCard components)
   */
  children: ReactNode;
}

export const OverviewSection = ({
  title = "Overview",
  columns = { base: 1, md: 2, lg: 3 },
  spacing = 6,
  children,
  showTitle = true,
}: OverviewSectionProps) => {
  const theme = useThemeTokens();

  return (
    <Box>
      {showTitle && (
        <Heading fontSize="xl" fontWeight="700" mb={6} color={theme.text.primary}>
          {title}
        </Heading>
      )}
      <SimpleGrid columns={columns} spacing={spacing}>
        {children}
      </SimpleGrid>
    </Box>
  );
};

